import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useBlogPosts } from '../utils/blogLoader';
import ErrorBoundary from '../components/ErrorBoundary';

export default function Blog() {
  const posts = useBlogPosts();

  return (
    <>
      <Helmet>
        <title>Blog - RailTech</title>
        <meta name="description" content="Latest insights and updates from RailTech on AI infrastructure and security." />
      </Helmet>

      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mx-auto max-w-2xl text-center"
          >
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Blog</h1>
            <p className="mt-2 text-lg leading-8 text-gray-600">
              Latest insights and updates from RailTech
            </p>
          </motion.div>

          <ErrorBoundary fallbackMessage="Unable to load blog posts. Please try again later.">
            <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {posts.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-600">No blog posts available at the moment.</p>
                </div>
              ) : (
                posts.map((post, index) => (
                  <motion.article
                    key={post.slug}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex flex-col items-start"
                  >
                    <div className="rounded-2xl bg-gray-50 p-8 hover:shadow-lg transition-shadow">
                      <div className="flex items-center gap-x-4 text-xs">
                        <time dateTime={post.date} className="text-gray-500">
                          {new Date(post.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </time>
                        <span className="relative z-10 rounded-full bg-gray-50 px-3 py-1.5 font-medium text-gray-600">
                          {post.category}
                        </span>
                      </div>
                      <div className="group relative">
                        <h3 className="mt-3 text-lg font-semibold leading-6 text-gray-900 group-hover:text-[#6C63FF]">
                          <Link to={`/blog/${post.slug}`}>
                            <span className="absolute inset-0" />
                            {post.title}
                          </Link>
                        </h3>
                        <p className="mt-5 line-clamp-3 text-sm leading-6 text-gray-600">{post.excerpt}</p>
                      </div>
                      <div className="relative mt-8 flex items-center gap-x-4">
                        <div className="text-sm leading-6">
                          <p className="font-semibold text-gray-900">
                            <span className="absolute inset-0" />
                            {post.author}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.article>
                ))
              )}
            </div>
          </ErrorBoundary>
        </div>
      </div>
    </>
  );
} 